import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { CATALYST_BACKOFFICEUSER_ROLES } from '../../../../utils/constants.js';

export function StaffCreate() {
  const navigate = useNavigate();

  const formCreateUser = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      status: true,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        email: yup
          .string()
          .email('Por favor insira um email válido')
          .required('Email é um campo obrigatório'),
        role: yup.string().required('Função / Role é um campo obrigatório'),
      }),
    ),
  });

  const mutationCreateStaffUser = useMutation({
    mutationFn: async (data) => {
      try {
        const { data: createUser } = await msCatalystApi.post(
          '/auth/register/backoffice',
          {
            firstName: data.firstName,
            lastName: data.lastName,
            fullName: `${data.firstName} ${data.lastName}`,
            email: data.email,
            type: 'Backoffice User',
            cc: `${Date.now()}_${data.email}`,
          },
        );

        const { data: createStaffUserRes } = await msCatalystApi.post(
          '/staff',
          {
            ...data,
            fullName: `${data.firstName} ${data.lastName}`,
            userId: createUser.data.ROWID,
          },
        );

        return createStaffUserRes.data;
      } catch (error) {
        throw error;
      }
    },
  });

  const onFormSubmit = async (data) => {
    try {
      const newUser = await mutationCreateStaffUser.mutateAsync({
        ...data,
      });

      toast.success('Utilizador criado com sucesso!');

      navigate(`/backoffice/admin/staff/${newUser?.ROWID}`);
    } catch (err) {
      if (
        err?.response?.data?.message ===
        'Duplicate value for email. Please give a different value'
      ) {
        toast.error('Já existe um utilizador com este email');
      } else {
        toast.error('Erro ao criar utilizador');
      }
    }
  };

  return (
    <FormUnsavedConfirmationWrapper form={formCreateUser}>
      <Heading className="pb-6">Criar Utilizador</Heading>

      <Form {...formCreateUser}>
        <form onSubmit={formCreateUser.handleSubmit(onFormSubmit)}>
          <div className="flex flex-col space-y-8">
            <FormCard>
              <FormField
                control={formCreateUser.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required={true}>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formCreateUser.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required={true}>Apelido</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formCreateUser.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required={true}>Email</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formCreateUser.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required={true}>Função / Role</FormLabel>
                    <FormControl>
                      <Select
                        disabled={formCreateUser.formState.isSubmitting}
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue="Utilizador"
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.keys(CATALYST_BACKOFFICEUSER_ROLES).map(
                            (key, i) => {
                              return (
                                <SelectItem
                                  key={i}
                                  value={CATALYST_BACKOFFICEUSER_ROLES[key]}
                                >
                                  {CATALYST_BACKOFFICEUSER_ROLES[key]}
                                </SelectItem>
                              );
                            },
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formCreateUser.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required={true}>Estado</FormLabel>
                    <div className="flex h-10 flex-row items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          disabled={formCreateUser.formState.isSubmitting}
                          id="status"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                      <span className="space-y-0 text-sm">Ativo</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormCard>
          </div>

          <FormFooter>
            <CancelButton />
            <Button
              type="submit"
              disabled={
                formCreateUser.formState.isSubmitting ||
                !formCreateUser.formState.isDirty
              }
              isLoading={formCreateUser.formState.isSubmitting}
            >
              <PlusIcon />
              Criar
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
